import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import CreateContentRow from "./CreateContentRow";
import OptimizeContentRow from "./OptimizeContentRow";
import WritingAssistantModal from "./WritingAssistantModal";
import { listMonitoringWritingAssistantRuns } from "../../../../services/monitoringApi";

const DEFAULT_VISIBLE = 5;
const runsRequestCache = new Map();

function useExistingRunsByRecommendation(websiteId, analysisId) {
	const [runsByRef, setRunsByRef] = useState(() => new Map());

	useEffect(() => {
		if (websiteId == null || analysisId == null) {
			setRunsByRef(new Map());
			return undefined;
		}
		let cancelled = false;
		const key = `${websiteId}:${analysisId}`;
		let request = runsRequestCache.get(key);
		if (!request) {
			request = listMonitoringWritingAssistantRuns(websiteId, { analysisId });
			runsRequestCache.set(key, request);
		}
		request
			.then((res) => {
				if (!res.ok) {
					runsRequestCache.delete(key);
					return;
				}
				if (cancelled) return;
				const next = new Map();
				res.runs.forEach((run) => {
					if (!next.has(run.recommendation_ref)) {
						next.set(run.recommendation_ref, run.id);
					}
				});
				setRunsByRef(next);
			})
			.catch(() => runsRequestCache.delete(key));
		return () => {
			cancelled = true;
		};
	}, [websiteId, analysisId]);

	return runsByRef;
}

function ShowMore({ totalCount, visibleCount, expanded, onToggle, noun }) {
	if (totalCount <= DEFAULT_VISIBLE && !expanded) return null;
	if (totalCount <= visibleCount && expanded) return null;
	return (
		<button
			type="button"
			onClick={onToggle}
			className="monitoring__analysis-strategy-show-more btn btn-link btn-sm p-0"
		>
			{expanded ? "Show fewer" : `View ${totalCount - visibleCount} more ${noun}`}
			{expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
		</button>
	);
}

export function CreateContentList({
	items,
	crossProviderSignals,
	websiteId,
	analysisId,
	onOpenWritingRun,
}) {
	const [expandedBrief, setExpandedBrief] = useState(() => new Set());
	const [showAll, setShowAll] = useState(false);
	const [writingRecommendation, setWritingRecommendation] = useState(null);
	const visible = showAll ? items : items.slice(0, DEFAULT_VISIBLE);
	const existingRuns = useExistingRunsByRecommendation(websiteId, analysisId);
	const handleCreated = (runId) => {
		runsRequestCache.delete(`${websiteId}:${analysisId}`);
		onOpenWritingRun?.(runId);
	};

	function toggleBrief(id) {
		setExpandedBrief((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	return (
		<div className="monitoring__analysis-strategy-rec-list">
			{visible.map((rec) => (
				<CreateContentRow
					key={rec.id}
					rec={rec}
					crossProviderSignals={crossProviderSignals}
					briefOpen={expandedBrief.has(rec.id)}
					onToggleBrief={() => toggleBrief(rec.id)}
					writingRunId={existingRuns.get(rec.id)}
					onUseWritingAssistant={() => setWritingRecommendation(rec)}
					onViewContent={onOpenWritingRun}
				/>
			))}
			{items.length === 0 && (
				<p className="monitoring__analysis-strategy-empty">
					No create-content recommendations.
				</p>
			)}
			<ShowMore
				totalCount={items.length}
				visibleCount={visible.length}
				expanded={showAll}
				onToggle={() => setShowAll((v) => !v)}
				noun="create recommendations"
			/>
			{writingRecommendation && (
				<WritingAssistantModal
					isOpen
					onClose={() => setWritingRecommendation(null)}
					onCreated={handleCreated}
					websiteId={websiteId}
					analysisId={analysisId}
					recommendationRef={writingRecommendation.id}
					mode="create"
					targetUrl={writingRecommendation.target?.suggested_url}
					title={writingRecommendation.title}
				/>
			)}
		</div>
	);
}

export function OptimizeContentList({
	items,
	crossProviderSignals,
	onViewGapAnalysis,
	websiteId,
	analysisId,
	onOpenWritingRun,
}) {
	const [expandedSignals, setExpandedSignals] = useState(() => new Set());
	const [showAll, setShowAll] = useState(false);
	const [writingRecommendation, setWritingRecommendation] = useState(null);
	const visible = showAll ? items : items.slice(0, DEFAULT_VISIBLE);
	const existingRuns = useExistingRunsByRecommendation(websiteId, analysisId);
	const handleCreated = (runId) => {
		runsRequestCache.delete(`${websiteId}:${analysisId}`);
		onOpenWritingRun?.(runId);
	};

	function toggleSignals(id) {
		setExpandedSignals((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	return (
		<div className="monitoring__analysis-strategy-rec-list">
			{visible.map((rec) => (
				<OptimizeContentRow
					key={rec.id}
					rec={rec}
					crossProviderSignals={crossProviderSignals}
					detailOpen={expandedSignals.has(rec.id)}
					onToggleDetail={() => toggleSignals(rec.id)}
					onViewGapAnalysis={onViewGapAnalysis}
					writingRunId={existingRuns.get(rec.id)}
					onUseWritingAssistant={() => setWritingRecommendation(rec)}
					onViewContent={onOpenWritingRun}
				/>
			))}
			{items.length === 0 && (
				<p className="monitoring__analysis-strategy-empty">
					No optimize recommendations.
				</p>
			)}
			<ShowMore
				totalCount={items.length}
				visibleCount={visible.length}
				expanded={showAll}
				onToggle={() => setShowAll((v) => !v)}
				noun="optimize recommendations"
			/>
			{writingRecommendation && (
				<WritingAssistantModal
					isOpen
					onClose={() => setWritingRecommendation(null)}
					onCreated={handleCreated}
					websiteId={websiteId}
					analysisId={analysisId}
					recommendationRef={writingRecommendation.id}
					mode="optimize"
					targetUrl={writingRecommendation.target?.url}
					title={writingRecommendation.title}
				/>
			)}
		</div>
	);
}
